import type { UnsubscribeConversation } from '@/helpers/types'
import { logger } from '@/logger/createLogger'
import { useMainStore } from '@/stores/main'
import { useResourcesStoreActions } from '@/stores/resources'
import { useEffect, useRef } from 'react'

/**
 * Hook to subscribe to new conversations and update resource store
 * when new messages are received.
 */

export const useSubscribeConversations = () => {
  const { fetchResource } = useResourcesStoreActions()
  const unsubscribeRef = useRef<UnsubscribeConversation | null>(null)
  const isSubscribingRef = useRef(false)
  const client = useMainStore(state => state.client)

  useEffect(() => {
    if (!client) return

    // Subscribe to any new messages and update the recents
    const subscribe = async () => {
      try {
        if (isSubscribingRef.current) return
        isSubscribingRef.current = true

        const result = await client.conversation.subscribe(newMsg => {
          logger.debug('New message received', newMsg)
          // TODO: Fetch and upsert only the specific conversation and resource
          if (newMsg.group_id) {
            void fetchResource({ id: newMsg.group_id })
          }

          if (newMsg.from_fabric_address_id) {
            void fetchResource({ id: newMsg.from_fabric_address_id })
          }
          // TODO: are there any other properties that should be used to fetch resources?
        })

        unsubscribeRef.current = result.unsubscribe
      } catch (error) {
        logger.error('Unable to subscribe to conversation', error)
        throw error
      } finally {
        isSubscribingRef.current = false
      }
    }

    void subscribe()

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
    }
  }, [client, fetchResource])
}
